import React from 'react';
import { Box, Typography } from '@mui/material'; // Imported useTheme
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyOutlined from '@mui/icons-material/HourglassEmptyOutlined';
import CachedOutlined from '@mui/icons-material/CachedOutlined';
import ReplayOutlined from '@mui/icons-material/ReplayOutlined';
import DoNotDisturbOnOutlined from '@mui/icons-material/DoNotDisturbOnOutlined';

export const statusConfig = {
	Pending: { icon: HourglassEmptyOutlined, color: '#29b6f6' },
	Recorded: { icon: CheckCircleOutline, color: '#66bb6a' },
	Reversed: { icon: DoNotDisturbOnOutlined, color: '#ef5350' },
	Returned: { icon: ReplayOutlined, color: '#ffa726' },
	// Set a default color for Unprocessed; our component logic will override it.
	Unprocessed: { icon: CachedOutlined, color: '#000' },
};

const FilterChip = ({ status, isActive, onChipClick }) => {
	const { icon: Icon, color: regularColor } = statusConfig[status] || { icon: Box, color: '#78909c' };

	let chipStyles;
	let finalIconColor;

	// --- SPECIAL LOGIC FOR THE "UNPROCESSED" CHIP ---
	if (status === 'Unprocessed') {
		const baseStyles = {
			display: 'inline-flex',
			alignItems: 'center',
			gap: 0.75,
			// RESPONSIVE CHANGE: Adjust padding for different screen sizes
			px: { xs: 1.5, sm: 2 },
			py: 1,
			borderRadius: '5px',
			cursor: 'pointer',
			transition: 'all 0.25s ease-in-out',
			border: '1px solid #000', // Always a black border
			// RESPONSIVE CHANGE: Use min-width on larger screens, and allow natural width on mobile
			minWidth: { sm: '110px' },
			// RESPONSIVE CHANGE: Allow chips to grow and fill space on small screens for a cleaner look
			flexGrow: { xs: 1, sm: 0 },
			justifyContent: 'center',
		};

		const activeStyles = {
			...baseStyles,
			backgroundColor: '#000',
			color: '#fff',
			boxShadow: '0 4px 15px -5px rgba(0,0,0,0.3)',
			transform: 'translateY(-2px)',
		};

		const inactiveStyles = {
			...baseStyles,
			backgroundColor: '#fff',
			color: '#000',
			opacity: 0.9,
			'&:hover': {
				backgroundColor: 'rgba(0, 0, 0, 0.04)',
			},
		};

		chipStyles = isActive ? activeStyles : inactiveStyles;
		finalIconColor = isActive ? '#fff' : '#000';
	} else {
		// --- DEFAULT LOGIC FOR ALL OTHER CHIPS ---
		const baseStyles = {
			display: 'inline-flex',
			alignItems: 'center',
			gap: 0.75,
			// RESPONSIVE CHANGE: Adjust padding for different screen sizes
			px: { xs: 1.5, sm: 2 },
			py: 1,
			borderRadius: '5px',
			cursor: 'pointer',
			transition: 'all 0.25s ease-in-out',
			border: `1px solid ${regularColor}`,
			// RESPONSIVE CHANGE: Use min-width on larger screens, and allow natural width on mobile
			minWidth: { sm: '110px' },
			// RESPONSIVE CHANGE: Allow chips to grow and fill space on small screens for a cleaner look
			flexGrow: { xs: 1, sm: 0 },
			justifyContent: 'center',
		};

		const activeStyles = {
			...baseStyles,
			backgroundColor: regularColor,
			color: '#fff',
			boxShadow: `0 4px 15px -5px ${regularColor}`,
			transform: 'translateY(-2px)',
		};

		const inactiveStyles = {
			...baseStyles,
			backgroundColor: 'transparent',
			color: regularColor,
			opacity: 0.8,
			'&:hover': {
				backgroundColor: 'rgba(0, 0, 0, 0.04)',
			},
		};

		chipStyles = isActive ? activeStyles : inactiveStyles;
		finalIconColor = isActive ? '#fff' : regularColor;
	}

	return (
		<Box sx={chipStyles} onClick={() => onChipClick(status)}>
			<Icon sx={{ fontSize: '18px', color: finalIconColor }} />
			<Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1, color: 'inherit' }}>
				{status}
			</Typography>
		</Box>
	);
};

// Main component updated for responsive layout
const StatusFilter = ({ statusesToRender = [], selectedStatuses = [], onFilterChange }) => {
	return (
		// RESPONSIVE CHANGE: The main container now centers items on mobile and aligns left on larger screens.
		<Box
			sx={{
				p: 1,
				mb: 1,
				display: 'flex',
				flexWrap: 'wrap',
				gap: 1.5,
				alignItems: 'center',
				justifyContent: { xs: 'center', sm: 'flex-start' },
			}}
		>
			<Typography
				variant="subtitle1"
				sx={{
					fontWeight: 600,
					// RESPONSIVE CHANGE: The label takes full width on mobile to push chips to the next line,
					// and has appropriate margins for each layout.
					width: { xs: '100%', sm: 'auto' },
					textAlign: { xs: 'center', sm: 'left' },
					mb: { xs: 1, sm: 0 }, // Margin bottom only on mobile
					mr: { xs: 0, sm: 2 }, // Margin right only on desktop
				}}
			>
				Filter by Status :
			</Typography>
			{statusesToRender.map((status) => (
				<FilterChip key={status} status={status} isActive={selectedStatuses.includes(status)} onChipClick={onFilterChange} />
			))}
		</Box>
	);
};

export default StatusFilter;